import React, { useMemo } from 'react';
import { convictionColor, convictionLabel } from '../../utils/convictionLabel';
import './KPICards.css';

export default function KPICards({ portfolioItems, scores, analysisResult }) {
  const kpis = useMemo(() => {
    if (!portfolioItems || portfolioItems.length === 0) return null;

    let totalValue = 0;
    let totalCost = 0;
    let bestMover = null;
    let worstMover = null;

    portfolioItems.forEach((item) => {
      const shares = item.shares || 0;
      const current = item.current_price || item.entry_price || 0;
      const entry = item.entry_price || current;
      const value = shares * current;
      const cost = shares * entry;

      totalValue += value;
      totalCost += cost;

      const pnlPct = entry > 0 ? ((current - entry) / entry) * 100 : 0;

      if (!bestMover || pnlPct > bestMover.pnlPct) {
        bestMover = { symbol: item.symbol, pnlPct };
      }
      if (!worstMover || pnlPct < worstMover.pnlPct) {
        worstMover = { symbol: item.symbol, pnlPct };
      }
    });

    const totalPnl = totalValue - totalCost;
    const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

    // Average conviction from scores
    let avgConviction = null;
    if (scores && scores.length > 0) {
      const valid = scores.filter((s) => s.combined_conviction != null);
      if (valid.length > 0) {
        avgConviction = valid.reduce((sum, s) => sum + s.combined_conviction, 0) / valid.length;
      }
    }

    return {
      totalValue,
      totalPnl,
      totalPnlPct,
      positions: portfolioItems.length,
      avgConviction,
      bestMover,
      worstMover,
      regime: analysisResult?.regime || null,
    };
  }, [portfolioItems, scores, analysisResult]);

  if (!kpis) return null;

  const formatCurrency = (val) => {
    if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
    if (Math.abs(val) >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
    return `$${val.toFixed(2)}`;
  };

  const formatPct = (val) => `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;

  return (
    <div className="kpi-cards">
      <div className="kpi-card">
        <span className="kpi-label">Portfolio Value</span>
        <span className="kpi-value">{formatCurrency(kpis.totalValue)}</span>
        <span className="kpi-sub">{kpis.positions} positions</span>
      </div>

      <div className="kpi-card">
        <span className="kpi-label">Total P&L</span>
        <span className={`kpi-value ${kpis.totalPnl >= 0 ? 'positive' : 'negative'}`}>
          {formatCurrency(kpis.totalPnl)}
        </span>
        <span className={`kpi-sub ${kpis.totalPnlPct >= 0 ? 'positive' : 'negative'}`}>
          {formatPct(kpis.totalPnlPct)}
        </span>
      </div>

      {kpis.avgConviction != null && (
        <div className="kpi-card">
          <span className="kpi-label">Avg Conviction</span>
          {/* Conviction is 0-100: this said "out of 5.0" and colored on 3.5/2.5,
              so every portfolio showed green (found 2026-11-03). */}
          <span className="kpi-value" style={{ color: convictionColor(kpis.avgConviction) }}>
            {kpis.avgConviction.toFixed(1)}
          </span>
          <span className="kpi-sub">{convictionLabel(kpis.avgConviction)} · out of 100</span>
        </div>
      )}

      {kpis.regime && (
        <div className="kpi-card">
          <span className="kpi-label">Market Regime</span>
          <span className={`kpi-value regime ${kpis.regime.toLowerCase().replace(/[\s-]/g, '-')}`}>
            {kpis.regime}
          </span>
          <span className="kpi-sub">energy state</span>
        </div>
      )}

      {kpis.bestMover && (
        <div className="kpi-card">
          <span className="kpi-label">Top Mover</span>
          <span className={`kpi-value ${kpis.bestMover.pnlPct >= 0 ? 'positive' : 'negative'}`}>
            {kpis.bestMover.symbol}
          </span>
          <span className={`kpi-sub ${kpis.bestMover.pnlPct >= 0 ? 'positive' : 'negative'}`}>
            {formatPct(kpis.bestMover.pnlPct)}
          </span>
        </div>
      )}

      {kpis.worstMover && kpis.worstMover.symbol !== kpis.bestMover?.symbol && (
        <div className="kpi-card">
          <span className="kpi-label">Worst Mover</span>
          <span className={`kpi-value ${kpis.worstMover.pnlPct >= 0 ? 'positive' : 'negative'}`}>
            {kpis.worstMover.symbol}
          </span>
          <span className={`kpi-sub ${kpis.worstMover.pnlPct >= 0 ? 'positive' : 'negative'}`}>
            {formatPct(kpis.worstMover.pnlPct)}
          </span>
        </div>
      )}
    </div>
  );
}
